'use client'

import React from 'react'
import { ExternalLink } from 'lucide-react'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import CollectionSocials from '@/components/collection-socials'
import CollectionChart from '../production-token-chart'
import BitcoinIcon from '@/icons/bitcoin-filled'
import PercentChange from '@/components/percent-change'
import ErrorBoundary from '@/components/error-boundary'

const getQuickViewChartWidth = (maxWidth: number) => {
	if (typeof window === 'undefined') return maxWidth
	return Math.min(window.innerWidth - 34, maxWidth)
}

export type QuickViewMetric = {
	label: string
	primary: React.ReactNode
	secondary?: React.ReactNode
	isBtc?: boolean
}

export interface QuickViewBodyProps {
	collection: any
	slug: string
	/** Optional actions rendered beneath the icon/name heading. */
	headerActions?: React.ReactNode
	metrics: QuickViewMetric[]
	description?: React.ReactNode
	priceDisplay: React.ReactNode
	/** USD price rendered inline with the change row so the secondary metric
	 *  reads as one line ("$9.85 +59.76% 1W"). Pass a string like "$9.85". */
	priceUsd?: React.ReactNode
	changeValue?: number
	changeLabel?: string
	/** Max pixel width for the embedded chart canvas. Defaults to 438 (modal). */
	chartMaxWidth?: number
	/** Omit the chart/price heading when the parent already renders it. */
	showOverview?: boolean
	/** Optional right-aligned slot inside the Market Stats heading row. */
	metricsHeaderRight?: React.ReactNode
	/** Bubbles the embedded chart's selected range up to an ancestor (e.g. the
	 * collection header) so external labels stay in sync with the chart. */
	onRangeChange?: (label: string, value: number) => void
}

/**
 * Renders the insights body (icon + name, price, chart, stats grid, about).
 * Used standalone (inline full-page on mobile) and inside CollectionQuickView
 * (the desktop modal).
 */
export function QuickViewBody({
	collection,
	slug,
	headerActions,
	metrics,
	description,
	priceDisplay,
	priceUsd,
	changeValue,
	changeLabel = '7D',
	chartMaxWidth = 438,
	showOverview = true,
	metricsHeaderRight,
	onRangeChange
}: QuickViewBodyProps) {
	const galleryInscriptionId =
		collection?.gallery_inscription_id || collection?.galleryInscriptionId || null
	const hasSocials = Boolean(
		collection?.socials?.website || collection?.socials?.twitter || collection?.socials?.discord
	)
	const hasLinks = Boolean(galleryInscriptionId || hasSocials)
	const [chartWidth, setChartWidth] = React.useState(() => getQuickViewChartWidth(chartMaxWidth))
	const [hoverPrice, setHoverPrice] = React.useState<string | null>(null)
	const [rangeState, setRangeState] = React.useState<{ label: string; value: number } | null>(null)
	const onRangeChangeRef = React.useRef(onRangeChange)
	React.useEffect(() => {
		onRangeChangeRef.current = onRangeChange
	}, [onRangeChange])
	const rangeStateRef = React.useRef<{ label: string; value: number } | null>(null)
	const handleChartRangeChange = React.useCallback((label: string, value: number) => {
		const current = rangeStateRef.current
		if (current?.label === label && Math.abs(current.value - value) < 0.000001) return
		const next = { label, value }
		rangeStateRef.current = next
		setRangeState(next)
		onRangeChangeRef.current?.(label, value)
	}, [])

	const displayLabel = rangeState?.label ?? changeLabel
	const displayChange = rangeState?.value ?? changeValue

	React.useEffect(() => {
		const onResize = () => setChartWidth(getQuickViewChartWidth(chartMaxWidth))
		onResize()
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [chartMaxWidth])

	return (
		<>
			{showOverview && (
				<>
					<div className="flex items-start gap-3 px-5 py-2">
						<CollectionIcon
							className="size-8 shrink-0 overflow-hidden rounded-full"
							collection={collection}
						/>
						<div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2">
							<h2 className="flex min-w-0 flex-1 items-center gap-1 text-base font-semibold text-foreground">
								<span className="truncate">{collection?.name}</span>
								<VerifiedBadge slug={slug} />
							</h2>
							{headerActions && (
								<div className="ml-auto flex shrink-0 flex-nowrap items-center gap-2">
									{headerActions}
								</div>
							)}
						</div>
					</div>

					<div className="px-5">
						<div className="text-[32px] font-semibold leading-none tracking-[-0.02em] text-foreground">
							{hoverPrice ?? priceDisplay}
						</div>
						{(priceUsd || typeof displayChange === 'number') && (
							<div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-none">
								{priceUsd && <span className="font-medium text-foreground/65">{priceUsd}</span>}
								{typeof displayChange === 'number' && (
									<span className="inline-flex items-baseline gap-1">
										<PercentChange value={displayChange} />
										<span className="text-[11px] text-muted-foreground">{displayLabel}</span>
									</span>
								)}
							</div>
						)}
					</div>

					<div className="relative mt-4 w-full">
						{slug ? (
							<ErrorBoundary>
								<CollectionChart
									slug={slug}
									width={chartWidth}
									height={180}
									minimal
									onHoverPrice={setHoverPrice}
									onRangeChange={handleChartRangeChange}
								/>
							</ErrorBoundary>
						) : null}
					</div>
				</>
			)}
			{metrics.length > 0 && (
				<div className="px-5 py-4">
					<div className="mb-3 grid grid-cols-2 items-center gap-x-6">
						<h3 className="text-[10px] font-semibold text-muted-foreground">
							Market Stats
						</h3>
						{metricsHeaderRight && <div>{metricsHeaderRight}</div>}
					</div>
					<div className="grid grid-cols-2 gap-x-6 gap-y-4">
						{metrics.map((m) => (
							<div key={m.label} className="flex flex-col gap-1 min-w-0">
								<div className="text-[11px] text-muted-foreground">{m.label}</div>
								<div className="flex min-w-0 items-center gap-1 text-sm font-semibold leading-none text-foreground">
									<span className="truncate">{m.primary}</span>
									{m.isBtc && <BitcoinIcon className="size-3 shrink-0" />}
								</div>
								{m.secondary && (
									<div className="text-[11px] leading-none text-muted-foreground">
										{m.secondary}
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			{(description || hasLinks) && (
				<div className="border-t border-border px-5 py-4">
					<h3 className="mb-2 text-[10px] font-semibold text-muted-foreground">
						About
					</h3>
					{description && <p className="text-sm leading-6 text-foreground/65">{description}</p>}
					{hasLinks && (
						<div className={description ? 'mt-3' : ''}>
							<CollectionSocials
								galleryInscriptionId={galleryInscriptionId}
								socials={collection?.socials}
								iconClassName="size-4"
							/>
						</div>
					)}
				</div>
			)}
		</>
	)
}

interface CollectionQuickViewProps extends QuickViewBodyProps {
	children: React.ReactNode
}

export function CollectionQuickView({ children, ...bodyProps }: CollectionQuickViewProps) {
	const { collection } = bodyProps
	const twitterHandle: string | undefined = collection?.socials?.twitter || collection?.twitter
	const twitterSearchUrl = twitterHandle
		? `https://x.com/${String(twitterHandle).replace(/^@/, '')}`
		: collection?.name
		? `https://x.com/search?q=${encodeURIComponent(collection.name)}`
		: null

	return (
		<Dialog>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="top-[5.5rem] max-h-[calc(100dvh-7rem)] w-[calc(100vw-2rem)] max-w-[440px] translate-y-0 gap-0 overflow-y-auto overflow-x-hidden border-border bg-card p-0 text-foreground sm:top-[50%] sm:max-h-[calc(100dvh-2rem)] sm:translate-y-[-50%] [&>button]:bg-transparent [&>button]:text-muted-foreground [&>button]:data-[state=open]:bg-transparent [&>button]:data-[state=open]:text-muted-foreground [&>button:hover]:text-foreground [&>button:focus]:ring-0 [&>button:focus]:ring-offset-0">
				<QuickViewBody {...bodyProps} />
				{twitterSearchUrl && (
					<a
						href={twitterSearchUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="mx-5 mb-4 mt-2 flex min-w-0 items-center gap-4 rounded-xl bg-foreground/[0.10] p-4 transition-colors hover:bg-foreground/[0.14]"
					>
						<svg viewBox="0 0 24 24" className="size-6 shrink-0 fill-foreground">
							<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
						</svg>
						<div className="min-w-0 flex-1 border-l border-border pl-4">
							<div className="truncate text-base font-semibold text-foreground">
								What&apos;s Happening with {collection?.name || 'this collection'}?
							</div>
							<div className="mt-0.5 flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
								See what people are saying on X
								<ExternalLink size={10} />
							</div>
						</div>
					</a>
				)}
			</DialogContent>
		</Dialog>
	)
}
